import { Component } from '@theme/component';
import { prefersReducedMotion } from '@theme/utilities';

/**
 * A pinned, scroll-driven slide stack. Progressive enhancement:
 * - Default: slides render normally in the document flow (no pinning).
 * - Enhanced: section is pinned while slides overlay and transition vertically.
 */
class PinnedScrollSlidesComponent extends Component {
  requiredRefs = ['viewport'];

  /** @type {number | null} */
  #rafId = null;

  /** @type {number} */
  #panelHeightPx = 0;

  /** @type {number} */
  #startScrollYPx = 0;

  /** @type {number} */
  #scrollRangePx = 0;

  /** @type {number} */
  #scrollSpacePx = 0;

  connectedCallback() {
    super.connectedCallback();
    this.#init();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#destroy();
    this.#init();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#destroy();
  }

  #init() {
    if (prefersReducedMotion()) return;

    const slides = this.#getSlides();
    if (!slides.length) return;

    this.dataset.enhanced = 'true';

    window.addEventListener('scroll', this.#onScroll, { passive: true });
    window.addEventListener('resize', this.#onResize, { passive: true });

    this.#recalculate();
    this.#update();
  }

  #destroy() {
    window.removeEventListener('scroll', this.#onScroll);
    window.removeEventListener('resize', this.#onResize);

    if (this.#rafId != null) {
      cancelAnimationFrame(this.#rafId);
      this.#rafId = null;
    }

    const slides = this.#getSlides();
    slides.forEach((slide) => {
      slide.style.removeProperty('transform');
      slide.style.removeProperty('z-index');
      slide.style.removeProperty('--pss-content-opacity');
    });

    delete this.dataset.enhanced;
    this.style.removeProperty('--pss-sticky-top');
    this.style.removeProperty('--pss-scroll-space');
  }

  #getSlides() {
    const slides = this.refs.slides;
    if (Array.isArray(slides)) return /** @type {HTMLElement[]} */ (slides.filter((el) => el instanceof HTMLElement));
    return [];
  }

  #onScroll = () => {
    if (this.#rafId != null) return;
    this.#rafId = requestAnimationFrame(() => {
      this.#rafId = null;
      this.#update();
    });
  };

  #onResize = () => {
    this.#recalculate();
    this.#update();
  };

  #recalculate() {
    const viewport = this.refs.viewport;
    if (!(viewport instanceof HTMLElement)) return;

    const headerHeightRaw = getComputedStyle(document.body).getPropertyValue('--header-height').trim();
    const headerHeightPx = Number.parseFloat(headerHeightRaw) || 0;
    this.style.setProperty('--pss-sticky-top', `${headerHeightPx}px`);

    // Measure panel height after applying sticky top variable.
    this.#panelHeightPx = viewport.getBoundingClientRect().height || 0;

    const slides = this.#getSlides();
    const slideCount = slides.length;

    this.#scrollRangePx = Math.max(0, slideCount - 1) * this.#panelHeightPx;
    this.#scrollSpacePx = this.#panelHeightPx + this.#scrollRangePx;
    this.style.setProperty('--pss-scroll-space', `${this.#scrollSpacePx}px`);

    const viewportRect = viewport.getBoundingClientRect();
    const viewportTopDoc = viewportRect.top + window.scrollY;
    // Start the animation only once the sticky viewport would "engage".
    this.#startScrollYPx = viewportTopDoc - headerHeightPx;

    // Ensure stacking order is always deterministic (higher index above).
    slides.forEach((slide, index) => {
      slide.style.zIndex = String(index + 1);
    });

    // Set a deterministic baseline to prevent any initial text layering flash.
    slides.forEach((slide, index) => {
      slide.style.transform = index === 0 ? 'translate3d(0, 0%, 0)' : 'translate3d(0, 100%, 0)';
    });
  }

  #update() {
    const slides = this.#getSlides();
    const slideCount = slides.length;
    if (!slideCount || this.#panelHeightPx <= 0) return;

    const scrollY = window.scrollY;
    const scrollPos = clamp(scrollY - this.#startScrollYPx, 0, this.#scrollRangePx);

    const fadeEnabled = this.dataset.fade === 'true';

    if (slideCount === 1) {
      const onlySlide = slides[0];
      onlySlide.style.transform = 'translate3d(0, 0%, 0)';
      if (fadeEnabled) onlySlide.style.setProperty('--pss-content-opacity', '1');
      return;
    }

    const maxSegmentIndex = Math.max(0, slideCount - 2);
    const rawSegmentIndex = Math.floor(scrollPos / this.#panelHeightPx);
    const segmentIndex = clamp(rawSegmentIndex, 0, maxSegmentIndex);
    const segmentStart = segmentIndex * this.#panelHeightPx;
    const segmentProgress = clamp((scrollPos - segmentStart) / this.#panelHeightPx, 0, 1);

    for (let i = 0; i < slideCount; i++) {
      const slide = slides[i];

      // Default: offscreen below.
      let translateYPercent = 100;
      let contentOpacity = 1;

      if (i < segmentIndex) {
        translateYPercent = 0;
      } else if (i === segmentIndex) {
        translateYPercent = 0;
      } else if (i === segmentIndex + 1) {
        translateYPercent = (1 - segmentProgress) * 100;
      }

      slide.style.transform = `translate3d(0, ${translateYPercent}%, 0)`;

      if (fadeEnabled) {
        if (i === segmentIndex + 1) {
          contentOpacity = segmentProgress;
        } else if (i > segmentIndex + 1) {
          contentOpacity = 0;
        }
        slide.style.setProperty('--pss-content-opacity', String(contentOpacity));
      }
    }
  }
}

function clamp(value, min, max) {
  return Math.min(max, Math.max(min, value));
}

if (!customElements.get('pinned-scroll-slides-component')) {
  customElements.define('pinned-scroll-slides-component', PinnedScrollSlidesComponent);
}
